/**
 * PostgreSQL Credential Form
 *
 * A comprehensive form for configuring PostgreSQL credentials with:
 * - Connection: host, database, port, username, password
 * - Advanced: SSL mode, schema, timeouts, pooling
 * Supports section-based rendering for tabbed display
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Loader2, Database } from 'lucide-react';
import { credentialsApi } from '@/lib/api';
import { toast } from 'sonner';

export interface PostgreSqlFormData {
  // Basic Connection
  host: string;
  database: string;
  username: string;
  password: string;
  port: string;

  // Advanced
  sslMode: string;
  schema: string;
  connectTimeout: string;
  commandTimeout: string;
  pooling: boolean;
  minPoolSize: string;
  maxPoolSize: string;
}

interface PostgreSqlCredentialFormProps {
  formData: PostgreSqlFormData;
  onChange: (data: Partial<PostgreSqlFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
  /** Which section to render - 'connection', 'advanced', or 'all' (default) */
  section?: 'connection' | 'advanced' | 'all';
}

export function PostgreSqlCredentialForm({
  formData,
  onChange,
  isEditing = false,
  section = 'all',
}: PostgreSqlCredentialFormProps) {
  const [fetchingDatabases, setFetchingDatabases] = useState(false);
  const [availableDatabases, setAvailableDatabases] = useState<string[]>([]);

  const handleChange = (field: keyof PostgreSqlFormData, value: any) => {
    onChange({ [field]: value });
  };

  const fetchDatabases = async () => {
    if (!formData.host || !formData.username) {
      toast.error('Please enter host and username first');
      return;
    }

    // Check if password is empty or masked (when editing existing credential)
    const maskedValue = '••••••••';
    if (!formData.password || formData.password === maskedValue) {
      toast.error('Please re-enter your password to fetch databases');
      return;
    }

    setFetchingDatabases(true);
    try {
      const response = await credentialsApi.listDatabases({
        type: 'postgresql',
        data: {
          host: formData.host,
          port: formData.port || '5432',
          username: formData.username,
          password: formData.password,
          sslMode: formData.sslMode,
        },
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        setAvailableDatabases(response.data);
        toast.success(`Found ${response.data.length} databases`);
      } else if (Array.isArray(response.data)) {
        toast.info('No databases found');
      } else {
        toast.error(response.data?.error || 'Failed to fetch databases');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Failed to fetch databases');
    } finally {
      setFetchingDatabases(false);
    }
  };

  // Connection section
  const connectionContent = (
    <div className="space-y-4">
      {/* Host */}
      <div className="space-y-2">
        <Label htmlFor="host" className="text-sm font-medium">
          Host <span className="text-red-500">*</span>
        </Label>
        <Input
          id="host"
          placeholder="localhost or postgres.example.com"
          value={formData.host}
          onChange={(e) => handleChange('host', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Port */}
      <div className="space-y-2">
        <Label htmlFor="port" className="text-sm font-medium">
          Port
        </Label>
        <Input
          id="port"
          placeholder="5432"
          value={formData.port}
          onChange={(e) => handleChange('port', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Database with dropdown */}
      <div className="space-y-2">
        <Label htmlFor="database" className="text-sm font-medium">
          Database <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          {availableDatabases.length > 0 ? (
            <Select
              value={formData.database}
              onValueChange={(value) => handleChange('database', value)}
            >
              <SelectTrigger id="database" className="flex-1 font-mono text-sm">
                <SelectValue placeholder="Select database" />
              </SelectTrigger>
              <SelectContent>
                {availableDatabases.map((db) => (
                  <SelectItem key={db} value={db}>
                    <div className="flex items-center gap-2">
                      <Database className="h-3 w-3" />
                      {db}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="database"
              placeholder="postgres"
              value={formData.database}
              onChange={(e) => handleChange('database', e.target.value)}
              className="flex-1 font-mono text-sm"
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={fetchDatabases}
            disabled={fetchingDatabases || !formData.host}
            title="Fetch available databases"
          >
            {fetchingDatabases ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter host and credentials, then click refresh to list available databases
        </p>
      </div>

      {/* Username and Password */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium">
            Username <span className="text-red-500">*</span>
          </Label>
          <Input
            id="username"
            placeholder="postgres"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            Password <span className="text-red-500">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={isEditing ? "Leave unchanged or enter new" : "Enter password"}
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            autoComplete="new-password"
            className="font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );

  // Advanced section
  const advancedContent = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sslMode" className="text-sm font-medium">
            SSL Mode
          </Label>
          <Select
            value={formData.sslMode || 'Prefer'}
            onValueChange={(value) => handleChange('sslMode', value)}
          >
            <SelectTrigger id="sslMode">
              <SelectValue placeholder="Select SSL mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Disable">Disable</SelectItem>
              <SelectItem value="Allow">Allow</SelectItem>
              <SelectItem value="Prefer">Prefer (Default)</SelectItem>
              <SelectItem value="Require">Require</SelectItem>
              <SelectItem value="VerifyCA">Verify CA</SelectItem>
              <SelectItem value="VerifyFull">Verify Full</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="schema" className="text-sm font-medium">
            Default Schema
          </Label>
          <Input
            id="schema"
            placeholder="public"
            value={formData.schema}
            onChange={(e) => handleChange('schema', e.target.value)}
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="connectTimeout" className="text-sm font-medium">
            Connection Timeout (seconds)
          </Label>
          <Input
            id="connectTimeout"
            type="number"
            placeholder="10"
            value={formData.connectTimeout}
            onChange={(e) => handleChange('connectTimeout', e.target.value)}
            min="0"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="commandTimeout" className="text-sm font-medium">
            Command Timeout (seconds)
          </Label>
          <Input
            id="commandTimeout"
            type="number"
            placeholder="30"
            value={formData.commandTimeout}
            onChange={(e) => handleChange('commandTimeout', e.target.value)}
            min="0"
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="pooling" className="text-sm font-medium">
            Connection Pooling
          </Label>
          <p className="text-xs text-muted-foreground">
            Enable for better performance
          </p>
        </div>
        <Switch
          id="pooling"
          checked={formData.pooling !== false}
          onCheckedChange={(checked) => handleChange('pooling', checked)}
        />
      </div>

      {formData.pooling !== false && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="minPoolSize" className="text-sm font-medium">
              Min Pool Size
            </Label>
            <Input
              id="minPoolSize"
              type="number"
              placeholder="1"
              value={formData.minPoolSize}
              onChange={(e) => handleChange('minPoolSize', e.target.value)}
              min="0"
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxPoolSize" className="text-sm font-medium">
              Max Pool Size
            </Label>
            <Input
              id="maxPoolSize"
              type="number"
              placeholder="100"
              value={formData.maxPoolSize}
              onChange={(e) => handleChange('maxPoolSize', e.target.value)}
              min="1"
              className="font-mono text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );

  // Return section-specific content or all
  if (section === 'connection') return connectionContent;
  if (section === 'advanced') return advancedContent;

  // Default: return all content (for backwards compatibility)
  return (
    <div className="space-y-6">
      {connectionContent}
      <div className="border-t pt-4">{advancedContent}</div>
    </div>
  );
}
